import { useRef, memo, useEffect } from "react";
import { Editor } from "@tinymce/tinymce-react";
import * as TinyMCETypes from "tinymce";
import { useAppSelector } from "../../App/State/Hooks/reduxHooks";
import { selectClientValue } from "../../App/State/Slices/SelectedClientSlice";
import { useDebouncedHandler } from "../../App/State/Hooks/useDebouncedHandler";
import { useAuthToken } from "../../App/State/Hooks/authHooks";

interface TinyEditorBaseProps {
  plugins: string[];
  toolbar: string | string[];
  functions?: (editor: any) => void;
  disabled?: boolean;
  textareaName?: string;
}

interface TinyEditorProps extends TinyEditorBaseProps {
  value: string;
  onEditorChange?: (newText: string, editor: TinyMCETypes.Editor) => void;
  onInit?: (editor: TinyMCETypes.Editor) => void;
}

interface TinyEditorPropsUncontrolled extends TinyEditorBaseProps {
  defaultValue?: string;
  editorRef: React.MutableRefObject<TinyMCETypes.Editor | null>;
  onEditorChange: (newText: string) => void;
}

const MAX_FILE_SIZE = 1024 * 1024;

let currentUploadToken = "";
const useUploadFileHandler = (clientId: string): ((blobInfo: any, progress: any) => Promise<string>) => {
  const token = useAuthToken();
  useEffect(() => {
    if (token) {
      currentUploadToken = token;
    }
  }, [token]);
  return (blobInfo, progress) => uploadFileHandler(clientId, blobInfo, progress, currentUploadToken);
};

const uploadFileHandler = (clientId: string, blobInfo: any, progress: any, token: string) => {
  return new Promise<string>((resolve, reject) => {
    if (blobInfo.blob().size > MAX_FILE_SIZE) {
      return reject({
        message: `File is too large. Size limit: ${MAX_FILE_SIZE / (1024 * 1024)}MB`,
        remove: true,
      });
    }

    if (!token) {
      return reject("There was an error uploading the file, please try again.");
    }

    const xhr = new XMLHttpRequest();
    xhr.withCredentials = false;
    xhr.open("POST", import.meta.env.VITE_BACKEND_API + "/api/media/upload/" + clientId);
    xhr.setRequestHeader("Authorization", "Bearer " + token);

    xhr.upload.onprogress = (e) => {
      progress((e.loaded / e.total) * 100);
    };

    xhr.onload = () => {
      if (xhr.status !== 200) {
        reject("There was an error uploading the file, please try again.");
        return;
      }

      const json = JSON.parse(xhr.responseText);

      if (!json || typeof json.location != "string") {
        reject("There was an error uploading the file, please try again.");
        return;
      }

      if (json.hasError) {
        reject(json.errorMessage);
        return;
      }

      resolve(json.location);
    };

    xhr.onerror = () => {
      reject("There was an error uploading the file, please try again.");
    };

    const formData = new FormData();
    formData.append("file", blobInfo.blob(), blobInfo.filename());

    xhr.send(formData);
  });
};

export const TinyEditor = ({
  value,
  plugins,
  toolbar,
  functions,
  disabled,
  textareaName,
  onEditorChange,
  onInit,
}: TinyEditorProps) => {
  const editorRef = useRef<TinyMCETypes.Editor | null>(null);
  const selectedClient = useAppSelector(selectClientValue);
  const imageUploadHandler = useUploadFileHandler(selectedClient.clientId);
  return (
    <Editor
      onInit={(evt, editor) => {
        onInit?.(editor);
        editorRef.current = editor;
      }}
      apiKey={import.meta.env.VITE_TINYMCE_KEY}
      disabled={disabled}
      textareaName={textareaName}
      value={value}
      onEditorChange={onEditorChange}
      init={{
        height: 450,
        selector: "textarea" as any,
        menubar: false,
        plugins: plugins,
        editable_class: "mceEditable",
        toolbar: toolbar,
        setup: functions,
        branding: false,
        valid_children: "+body[style]", // Allow style tag in body for custom styles
        images_file_types: "jpeg,jpg,png,gif,webp",
        images_upload_handler: (blobInfo, progress) => imageUploadHandler(blobInfo, progress),
        quickbars_insert_toolbar: false,
      }}
    />
  );
};

export function TinyEditorUncontrolled({
  defaultValue,
  editorRef,
  plugins,
  toolbar,
  functions,
  disabled,
  textareaName,
  onEditorChange,
}: TinyEditorPropsUncontrolled) {
  const selectedClient = useAppSelector(selectClientValue);
  const imageUploadHandler = useUploadFileHandler(selectedClient.clientId);
  const handleChangeContent = () => {
    if (editorRef.current) {
      const currentContent = editorRef.current.getContent();
      onEditorChange(currentContent);
    }
  };
  const handleChangeContentDebounced = useDebouncedHandler(handleChangeContent, 100);
  return (
    <Editor
      onInit={(_, editor) => {
        editorRef.current = editor;
      }}
      apiKey={import.meta.env.VITE_TINYMCE_KEY}
      disabled={disabled}
      textareaName={textareaName}
      onEditorChange={(e, _v) => handleChangeContentDebounced(e)}
      init={{
        height: 450,
        selector: "textarea" as any,
        menubar: false,
        plugins: plugins,
        editable_class: "mceEditable",
        toolbar: toolbar,
        setup: functions,
        branding: false,
        valid_children: "+body[style]", // Allow style tag in body for custom styles
        images_file_types: "jpeg,jpg,png,gif,webp",
        images_upload_handler: (blobInfo, progress) => imageUploadHandler(blobInfo, progress),
        quickbars_insert_toolbar: false,
        init_instance_callback: (editor) => {
          if (defaultValue) {
            editor.setContent(defaultValue);
          }
          editor.on("SetContent", (e) => {
            e.stopPropagation();
            handleChangeContent();
          });
        },
      }}
    />
  );
}

export default memo(TinyEditor);
